import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useQuery } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { PlayerProfileDialog } from "./player-profile-dialog";
import { apiRequest } from "@/lib/queryClient";


interface PlayerAvatarProps {
  player: { id: number; username: string };
  isActive?: boolean;
  isConnected?: boolean;
  isOpponent?: boolean;
  showControls?: boolean;
  size?: "sm" | "md" | "lg";
}

interface AvatarData {
  imageUrl: string | null;
}

export function PlayerAvatar({
  player,
  isActive,
  isConnected,
  isOpponent,
  showControls,
  size = "md",
}: Readonly<PlayerAvatarProps>) {
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  
  // Special handling for AI player (id === 0)
  const isAIPlayer = player.id === 0;
  const aiAvatarUrl = "https://teacherwilliam.sgp1.cdn.digitaloceanspaces.com/spar/spar-legends.jpeg";

  // No additional code needed here - avatar handling is managed by useQuery below
  
  const { data: avatarData } = useQuery<AvatarData>({
    queryKey: ["avatar", player.id],
    queryFn: async () => {
      if (isAIPlayer) {
        return { imageUrl: aiAvatarUrl };
      }
      
      try {
        let apiUrl = "";
        if (Capacitor.isNativePlatform()) {
          const serverUrl = import.meta.env.VITE_SERVER_URL ?? 'https://sparlegends.com';
          apiUrl = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;
        }

        // First get the user's profile to get avatarId and source
        const profileRes = await fetch(`${apiUrl}/api/profile/${player.id}`);
        if (!profileRes.ok) return { imageUrl: null };
        
        const profile = await profileRes.json();
        if (!profile?.profile?.avatarId) return { imageUrl: null };
        
      
        // Construct endpoint based on avatarSource
        const source = profile.profile.avatarSource || "default";
        const endpoint = source === "shop" 
          ? `/api/customization/shopItem/${profile.profile.avatarId}` 
          : `/api/customization/avatars/${profile.profile.avatarId}`;
          
        const avatarRes = await apiRequest("GET", `${apiUrl}${endpoint}`);
        if (!avatarRes.ok) return { imageUrl: null };
        
        const avatarData = await avatarRes.json();
        let imageUrl = avatarData.imageUrl;
        
        // Handle mobile URLs
        if (Capacitor.isNativePlatform() && imageUrl?.startsWith('/')) {
          imageUrl = `${apiUrl}${imageUrl}`;
        }
        
        return { imageUrl };
      } catch (error) {
        console.error(`Error fetching avatar for player ${player.id}:`, error);
        return { imageUrl: null };
      }
    },
    // Fixed: Now AI player (id=0) will correctly enable the query
    enabled: isAIPlayer || !!player.id,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  // Create an immediate value for AI avatar to bypass React Query
  const effectiveAvatarData = isAIPlayer 
    ? { imageUrl: aiAvatarUrl } 
    : avatarData;

  const initials = player.username
    ? player.username.split(" ").map((n) => n[0]).join("").toUpperCase()
    : "??";

  const sizeClasses = {
    sm: "h-8 w-8 text-sm",
    md: "h-12 w-12 text-lg",
    lg: "h-16 w-16 text-xl",
  };

  const handleAvatarClick = () => {
    setShowProfileDialog(true);
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative">
        <Avatar
          className={cn(
            sizeClasses[size],
            isActive && "ring-2 theme-ring theme-ring-offset-2",
            isOpponent && "ring-1 ring-indigo-500/70",
            "cursor-pointer hover:opacity-90 transition-opacity"
          )}
          onClick={handleAvatarClick}
        >
          {effectiveAvatarData?.imageUrl && 
            <AvatarImage 
              src={effectiveAvatarData.imageUrl} 
              alt={player.username} 
            />
          }
          <AvatarFallback className={cn(
            isOpponent ? "bg-indigo-900/30" : "theme-bg-primary/20",
            "font-medium"
          )}>
            {initials}
          </AvatarFallback>
        </Avatar>
        {isConnected !== undefined && (
          <div
            className={cn(
              "absolute bottom-0 right-0 h-3 w-3 rounded-full",
              "shadow-md",
              isConnected ? "bg-emerald-500" : "bg-red-500",
              size === "sm" && "h-2 w-2",
              size === "lg" && "h-4 w-4",
            )}
          />
        )}
      </div>
      
      <PlayerProfileDialog 
        playerId={player.id}
        playerUsername={player.username}
        isOpen={showProfileDialog}
        onOpenChange={setShowProfileDialog}
      />
    </div>
  );
}



/*

const queryClient = useQueryClient();
  const { data: avatarData } = useQuery<AvatarData>({
    queryKey: ["avatar", player.id],
    queryFn: async () => {
      if (isAIPlayer) {
        return { imageUrl: aiAvatarUrl };
      }
      
      try {
        let apiUrl = "";
        if (Capacitor.isNativePlatform()) {
          const serverUrl = import.meta.env.VITE_SERVER_URL ?? 'https://sparlegends.com';
          apiUrl = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;
        }

        // First get the user's profile to get avatarId and source
        const profileRes = await fetch(`${apiUrl}/api/profile/${player.id}`);
        if (!profileRes.ok) return { imageUrl: null };
        
        const profile = await profileRes.json();
        if (!profile?.profile?.avatarId) return { imageUrl: null };
        
      
        // Construct endpoint based on avatarSource
        const source = profile.profile.avatarSource || "default";
        const endpoint = source === "shop" 
          ? `/api/customization/shopItem/${profile.profile.avatarId}` 
          : `/api/customization/avatars/${profile.profile.avatarId}`;
          
        const avatarRes = await apiRequest("GET", `${apiUrl}${endpoint}`);
        if (!avatarRes.ok) return { imageUrl: null };
        
        const avatarData = await avatarRes.json();
        let imageUrl = avatarData.imageUrl;
        
        // Handle mobile URLs
        if (Capacitor.isNativePlatform() && imageUrl?.startsWith('/')) {
          imageUrl = `${apiUrl}${imageUrl}`;
        }
        
        return { imageUrl };
      } catch (error) {
        console.error(`Error fetching avatar for player ${player.id}:`, error);
        return { imageUrl: null };
      }
    },
    enabled: isAIPlayer || !!player.id,
    staleTime: 0, // Remove stale time to always fetch fresh data
    cacheTime: 0, // Remove cache time to force new fetches
    retry: 2,
  });

  */
